/**
 * PGSS-5748: what a box label shows. PGSS-5824: 6" wide × 4" tall, in the layout of the label PSG uses today. Pure helpers, so the label's rules are tested without rendering it.
 * Agreed with PSG on 18 Oct 2026: customer code at the top, QR code of the box code with the code printed under it, and
 * "USA VAS" / "PLANT VAS" at the bottom for an order with VAS. The classic label's company header and its labelled
 * colour / shape / finish and LEN / THK / HGT cells stay on it too.
 */

/** The box code as printed: groups of four, e.g. 7K3M-9QX2-TA. Anything that isn't a 10-character code is left as is. */
export function formatBoxCode(code) {
  if (typeof code !== 'string' || code.length !== 10) return code || '';
  return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
}

/** "USA VAS" or "PLANT VAS" for an order with VAS, otherwise nothing. */
export function vasText(vasLocation) {
  if (vasLocation === 'USA') return 'USA VAS';
  if (vasLocation === 'PLANT') return 'PLANT VAS';
  return null;
}

/**
 * PGSS-5792: what the customer block shows. The customer's code; "Inventory" for PSG stock (no customer, flagged by
 * the API as is_inventory); otherwise a dash, as for a customer with no code.
 */
export function customerText(label) {
  // PGSS-5824: the API says what prints: the code, or the name when the company setting is on.
  if (label?.customer_label) return label.customer_label;
  if (label?.customer_code) return label.customer_code;
  return label?.is_inventory ? 'Inventory' : '—';
}

/** Lines break between words, so a shrunk value needs about a tenth more room than its bare character count. */
const WRAP_ROOM = 1.1;

/**
 * PGSS-5748 (QA, 21 Oct): a long value shrinks to fit its cell instead of being cut with "…". A cell holds about
 * `charsPerLine` characters on each of `lines` lines at `basePx`. At a smaller size s, a line holds basePx / s times
 * as many characters, and the cell's height holds floor(lines * basePx / s) whole lines. The size starts from the
 * square-root estimate and steps down until those whole lines hold the text with a little room for words that wrap
 * early (the normal size needs that room too); the line limit is then everything the cell holds at that size, never more, so a long value can't push the
 * label onto a second page. It never goes below `minPx`; only text too long even at that size is cut.
 */
export function fitText(text, { basePx, charsPerLine, lines, minPx = 8 }) {
  const length = String(text ?? '').length;
  if (length * WRAP_ROOM <= charsPerLine * lines) return { fontPx: basePx, lines };
  const linesAt = (px) => Math.floor((lines * basePx) / px);
  const holds = (px) => linesAt(px) * ((charsPerLine * basePx) / px) >= length * WRAP_ROOM;
  let fontPx = Math.max(minPx, Math.floor(basePx * Math.sqrt((charsPerLine * lines) / length)));
  while (fontPx > minPx && !holds(fontPx)) fontPx -= 1;
  return { fontPx, lines: Math.max(lines, linesAt(fontPx)) };
}

/** The value the QR code holds: the box code alone, never a name or price. */
export function qrValue(label) {
  return label?.box_code || '';
}

/**
 * The order number a label shows: the customer order's, PSG's own number for it (a staff print from the order sends
 * it as customer_order_no). The supplier print sends only order_no, which is the same number (customer order and its
 * supplier order share it), so the supplier never sees anything new.
 */
export function orderNumberText(label) {
  const number = label?.customer_order_no || label?.order_no;
  return number ? `#${number}` : '';
}

/** PGSS-5824: the date on the label, printed that day in PSG's time zone, e.g. 10.27.26. */
export function labelDate(date = new Date()) {
  const part = (options) => date.toLocaleDateString('en-US', { timeZone: 'America/New_York', ...options });
  return `${part({ month: '2-digit' })}.${part({ day: '2-digit' })}.${part({ year: '2-digit' })}`;
}

/** PGSS-5824: stock goods carry a large INV: PSG's own stock, or a stock piece going out on a customer's order. */
export function isInventoryLabel(label) {
  return Boolean(label?.is_inventory || label?.piece);
}

/** A size as written on the product: 36.00 reads 36. */
const sizePart = (value) => (Number.isNaN(Number(value)) ? String(value).trim() : String(Number(value)));

/**
 * A name that already spells a size, e.g. 36x6x22, 36" x 6", 36×6×22 or 36in x 6in. Its figures are the name's own, so a
 * second size is not added.
 */
const HAS_SIZE = /\d\s*(?:["'\u2033]|in(?:ch(?:es)?)?\.?)?\s*[x\u00d7]\s*\d/i;

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The finish is in the name as a word of its own: "P5" is not in "P50", nor "S" in "Sawn" (review of #2818). */
export const hasWord = (name, word) =>
  new RegExp(`(^|[^a-z0-9])${escapeRegExp(String(word).trim())}($|[^a-z0-9])`, 'i').test(name);

/**
 * PGSS-5824: the product on one line, e.g. "India Black Die 36x6x22 P5". The product's name normally reads that way; a
 * custom or older name that leaves out the size or the finish gets them added, as the label has no other cell for them
 * (review of #2818).
 */
export function productLine(label) {
  const size = [label?.length, label?.width, label?.height].filter(Boolean).map(sizePart).join('x');
  const name = label?.name;
  if (!name) return [label?.color, label?.type, size, label?.polish].filter(Boolean).join(' ');
  const hasPolish = !String(label?.polish ?? '').trim() || hasWord(name, label.polish);
  return [name, HAS_SIZE.test(name) ? '' : size, hasPolish ? '' : label.polish].filter(Boolean).join(' ');
}

/**
 * One physical box of an order. Box numbers restart per container and two lines can share one, so a box is its container
 * and number, not its code (each line has its own). A piece has its own code. A label with neither is on its own.
 */
function boxKey(label, index) {
  if (label?.piece) return label?.box_code || `line-${index}`;
  // Trimmed: a container's print can send " 4" for box 4 (review of #2818).
  const boxNumber = String(label?.box_number ?? '').trim();
  if (boxNumber) return `${label?.container_number || ''}|${boxNumber}`;
  return label?.box_code || `line-${index}`;
}

/** A box number as a number, or NaN when it isn't a plain one ("3,3", "2 of 6", blank). */
function boxNumberValue(label) {
  const text = String(label?.box_number ?? '').trim();
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
}

/** Plain numbers ascending, then everything else, which keeps its place. */
export function compareBoxNumbers(a, b) {
  const aPlain = Number.isFinite(a);
  const bPlain = Number.isFinite(b);
  if (aPlain && bPlain) return a - b;
  if (aPlain) return -1;
  if (bPlain) return 1;
  return 0;
}

/**
 * PGSS-5824: numbers each order's boxes, 1 of N, lowest box number first. A box number runs across the container (an
 * order can have boxes 11 and 29), so it can't be the count. Run on every label before any are picked to print, so
 * reprinting one box still says 2/3. Two labels of one box share a count, and pieces count across the order, not the
 * line (review of #2818). It counts what was asked for: a container's print can't see the order's boxes on another.
 */
export function withBoxCounts(labels) {
  const list = labels || [];
  const orderOf = (label) => label?.customer_order_no || label?.order_no || '';
  const boxes = new Map();
  list.forEach((label, index) => {
    if (!boxes.has(orderOf(label))) boxes.set(orderOf(label), new Map());
    if (!boxes.get(orderOf(label)).has(boxKey(label, index))) boxes.get(orderOf(label)).set(boxKey(label, index), boxNumberValue(label));
  });
  const counts = new Map();
  boxes.forEach((inOrder, order) => {
    // Plain numbers first, lowest first. Numbers that aren't plain ("3,3", "2 of 6") come after them, in the order the API
    // sent them: the sort is stable, and the comparison stays consistent when the two kinds are mixed (review of #2818).
    const sorted = [...inOrder.entries()].sort(([, a], [, b]) => compareBoxNumbers(a, b)).map(([key]) => key);
    counts.set(order, new Map(sorted.map((key, place) => [key, { of: place + 1, total: sorted.length }])));
  });
  return list.map((label, index) => {
    const count = counts.get(orderOf(label)).get(boxKey(label, index));
    return { ...label, box_of: count.of, box_total: count.total };
  });
}

/** The count at the top of the label: "1/3" counted across the order, else a piece's "2/6" or the box number. */
export function boxCountText(label) {
  if (label?.box_total) return `${label.box_of}/${label.box_total}`;
  return String(label?.box_number ?? '').replace(' of ', '/');
}
